// /admin/alumnos-top — ranking de alumnos por asistencias confirmadas.
//
// Filtros: dias (default 90), limit (default 20). Tabla con nombre, email,
// total de clases, última visita. Útil para identificar fieles, detectar
// churn temprano y preparar emails de cuidado/renovación.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Loader2, AlertCircle, UserCircle, UserCheck } from 'lucide-react';

import { getAlumnosTop } from '../../lib/adminApi';

function localePara(lang) {
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return map[lang] || 'es-ES';
}

function fmtFecha(iso, lang) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return new Intl.DateTimeFormat(localePara(lang), {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(d);
}

function diasDesde(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return Math.round((Date.now() - d.getTime()) / 86400000);
}

const RANGOS = [30, 60, 90, 180, 365];

export default function AdminAlumnosTop() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [dias, setDias] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAlumnosTop({ dias, limit: 50 })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dias]);

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-titulo">
            <TrendingUp size={22} strokeWidth={1.5} /> {t('admin.alumnos_top.titulo')}
          </h1>
          <p className="admin-page-sub">{t('admin.alumnos_top.sub')}</p>
        </div>
        <div className="admin-filtros-inline">
          <label className="admin-label">
            {t('admin.alumnos_top.rango')}{' '}
            <select value={dias} onChange={(e) => setDias(parseInt(e.target.value, 10))}>
              {RANGOS.map((d) => (
                <option key={d} value={d}>{t('admin.alumnos_top.ultimos_dias', { dias: d })}</option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {loading && (
        <div className="admin-loading"><Loader2 size={20} className="spin" /> <span>{t('admin.cargando')}</span></div>
      )}
      {error && (
        <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>
      )}
      {!loading && !error && data && data.data.length === 0 && (
        <div className="admin-empty">{t('admin.alumnos_top.vacio', { dias })}</div>
      )}
      {!loading && !error && data && data.data.length > 0 && (
        <div className="admin-card">
          <table className="admin-tabla">
            <thead>
              <tr>
                <th>#</th>
                <th>{t('admin.alumnos_top.col_alumno')}</th>
                <th>{t('admin.alumnos_top.col_email')}</th>
                <th className="admin-col-num">{t('admin.alumnos_top.col_total')}</th>
                <th>{t('admin.alumnos_top.col_ultima')}</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((a, i) => {
                const dDesde = diasDesde(a.ultima_clase);
                const churn = dDesde !== null && dDesde >= 21;
                const nombre = [a.nombre, a.apellidos].filter(Boolean).join(' ').trim()
                  || a.email
                  || t('admin.alumnos_top.sin_nombre');
                return (
                  <tr key={a.usuario_id || a.email || i}>
                    <td className="admin-col-num">{i + 1}</td>
                    <td>
                      <span className="admin-cell-flex">
                        {a.es_guest
                          ? <UserCircle size={16} strokeWidth={1.5} />
                          : <UserCheck size={16} strokeWidth={1.5} />}
                        <span>{nombre}</span>
                        {a.es_guest && (
                          <span className="admin-pill admin-pill--neutro">{t('admin.alumnos_top.guest')}</span>
                        )}
                      </span>
                    </td>
                    <td className="admin-celda-tenue">{a.email || '—'}</td>
                    <td className="admin-col-num"><strong>{a.total_clases}</strong></td>
                    <td>
                      <span>{fmtFecha(a.ultima_clase, lang)}</span>
                      {dDesde !== null && (
                        <span className={'admin-cell-meta' + (churn ? ' admin-cell-meta--clay' : '')}>
                          {' · '}{t('admin.alumnos_top.hace_dias', { dias: dDesde })}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
